import React, { FC, useEffect, useState } from "react"
import { KButton, KSpan, KTooltip } from "kahuna-base-react-components"
import { systems } from "../../../../../apis"
import { EmailTemplate, TakedownLog } from "../../../../../types"
import { toast } from "react-toastify"
import { EMAIL_TEMPLATE_LANGUAGES, EMAIL_TEMPLATE_PLACEHOLDER, errorFilledToastOptions } from "../../../../../constants"

// Most mail clients silently truncate long mailto links, so the body is only inlined while it stays short.
const MAILTO_MAX_LENGTH = 1900

type TakedownEmailTemplatesProps = {
  takedownLog: TakedownLog
  // Resolves with the UPC list to inject into the template, or null when the user dismisses the selection.
  getUpcList: () => Promise<string | null>
}

const TakedownEmailTemplates: FC<TakedownEmailTemplatesProps> = (props) => {
  const [templateList, setTemplateList] = useState<EmailTemplate[]>([])
  const [selectedLang, setSelectedLang] = useState<string>(EMAIL_TEMPLATE_LANGUAGES[0].value)

  useEffect(() => {
    systems(`/email-template/`, "GET")
      .then((resp) => setTemplateList(resp.data.results))
      .catch(() => toast.error("Error fetching email templates", errorFilledToastOptions))
  }, [])

  const visibleTemplateList = templateList.filter((template) => template.lang === selectedLang)

  const fillPlaceholders = (content: string, artistName: string, upcList: string) =>
    content
      .split(EMAIL_TEMPLATE_PLACEHOLDER.ARTIST_NAME)
      .join(artistName)
      .split(EMAIL_TEMPLATE_PLACEHOLDER.UPC_LIST)
      .join(upcList)

  const onTemplateClick = async (template: EmailTemplate) => {
    const user = props.takedownLog.user
    const artistName = `${user?.firstName || ""} ${user?.lastName || ""}`.trim()

    let upcList = ""
    if (`${template.subject}${template.body}`.includes(EMAIL_TEMPLATE_PLACEHOLDER.UPC_LIST)) {
      const selectedUpcList = await props.getUpcList()
      if (selectedUpcList === null) {
        return
      }
      if (!selectedUpcList) {
        toast.info("No UPCs found for the selected option.")
        return
      }
      upcList = selectedUpcList
    }

    const subject = fillPlaceholders(template.subject, artistName, upcList)
    const body = fillPlaceholders(template.body, artistName, upcList)

    navigator.clipboard.writeText(`${subject}\n\n${body}`)

    const mailtoLink = `mailto:${user?.email || ""}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(
      body
    )}`

    if (mailtoLink.length > MAILTO_MAX_LENGTH) {
      window.location.href = `mailto:${user?.email || ""}?subject=${encodeURIComponent(subject)}`
      toast.success(`${template.name} copied to clipboard. Paste it into the draft, the body is too long for mailto.`)
      return
    }

    window.location.href = mailtoLink
    toast.success(`${template.name} copied to clipboard.`)
  }

  if (templateList.length === 0) {
    return null
  }

  return (
    <div className="flex gap-2 items-center">
      {visibleTemplateList.map((template) => (
        <KTooltip
          key={template.id}
          position="bottom"
          content={
            <div className="w-max max-w-[320px]">
              <KSpan text={template.description || template.name} fontSize={12} />
            </div>
          }
        >
          <KButton
            width="150px"
            text={template.name}
            onClick={() => onTemplateClick(template)}
            background="white"
            textColor="#111"
          />
        </KTooltip>
      ))}
      <div className="flex gap-1">
        {EMAIL_TEMPLATE_LANGUAGES.map((language) => (
          <KButton
            key={language.value}
            width="45px"
            text={language.value.toUpperCase()}
            background={selectedLang === language.value ? "black" : "white"}
            textColor={selectedLang === language.value ? "white" : "#111"}
            onClick={() => setSelectedLang(language.value)}
          />
        ))}
      </div>
    </div>
  )
}

export default TakedownEmailTemplates
